import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Flag, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { FullLoader } from "@/components/ProtectedRoute";
import { api, errMsg } from "@/lib/api";

const LETTERS = "ABCDE";
const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

function QuestionGrid({ questions, answers, flags, cur, setCur }) {
  return (
    <div className="grid grid-cols-5 gap-2" data-testid="cbt-question-grid">
      {questions.map((q, i) => {
        const cls = flags[q.id] ? "bg-amber-400 text-slate-900 border-amber-400" : answers[q.id] !== undefined ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200";
        return (
          <button key={q.id} onClick={() => setCur(i)} className={`h-10 rounded-md border font-mono text-sm font-semibold ${cls} ${cur === i ? "ring-2 ring-offset-2 ring-amber-500" : ""}`} data-testid={`cbt-nav-${i + 1}`}>
            {i + 1}
          </button>
        );
      })}
    </div>
  );
}

export default function ExamTake() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState({});
  const [cur, setCur] = useState(0);
  const [left, setLeft] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const answersRef = useRef({});
  const doneRef = useRef(false);
  const startRef = useRef(Date.now());
  answersRef.current = answers;

  useEffect(() => {
    api.get(`/student/exams/${id}/start`).then(({ data: d }) => {
      setData(d);
      setLeft(d.exam.duration_min * 60);
      startRef.current = Date.now();
    }).catch((e) => {
      toast.error(errMsg(e));
      nav("/siswa/ujian");
    });
  }, [id, nav]);

  const submit = useCallback(async () => {
    if (doneRef.current) return;
    doneRef.current = true;
    try {
      const { data: att } = await api.post(`/student/exams/${id}/submit`, { answers: answersRef.current, duration_sec: Math.round((Date.now() - startRef.current) / 1000) });
      toast.success(`Ujian selesai. Nilai Anda: ${att.score}`);
      nav(`/siswa/hasil/${att.id}`, { replace: true });
    } catch (e) {
      doneRef.current = false;
      toast.error(errMsg(e));
    }
  }, [id, nav]);

  useEffect(() => {
    if (left === null) return undefined;
    if (left <= 0) {
      submit();
      return undefined;
    }
    const t = setTimeout(() => setLeft((l) => l - 1), 1000);
    return () => clearTimeout(t);
  }, [left, submit]);

  if (!data) return <FullLoader />;
  const qs = data.questions;
  const q = qs[cur];
  const answered = Object.keys(answers).length;

  return (
    <div data-testid="cbt-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div><p className="eyebrow">Ujian CBT</p><h1 className="text-2xl font-bold text-slate-900 mt-1">{data.exam.title}</h1></div>
        <div className={`flex items-center gap-2 rounded-md px-4 py-2 font-mono text-xl font-bold ${left < 60 ? "bg-red-600 text-white" : "bg-slate-900 text-white"}`} data-testid="cbt-timer"><Timer className="h-5 w-5" />{mmss(Math.max(0, left))}</div>
      </div>
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 rounded-lg border border-slate-200 bg-white p-6">
          <p className="text-xs font-mono text-slate-500">SOAL {cur + 1} / {qs.length}</p>
          <p className="mt-3 text-lg text-slate-900 leading-relaxed whitespace-pre-line" data-testid="cbt-question-text">{q.text}</p>
          <div className="mt-6 space-y-3">
            {q.options.map((opt, i) => (
              <button key={i} onClick={() => setAnswers({ ...answers, [q.id]: i })} className={`w-full text-left flex gap-3 items-start rounded-md border p-4 transition-colors duration-150 ${answers[q.id] === i ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-400"}`} data-testid={`cbt-option-${LETTERS[i]}`}>
                <span className={`h-7 w-7 shrink-0 rounded flex items-center justify-center font-mono text-sm font-bold ${answers[q.id] === i ? "bg-amber-500 text-slate-900" : "bg-slate-100 text-slate-600"}`}>{LETTERS[i]}</span>
                <span className="text-sm text-slate-800 pt-1">{opt}</span>
              </button>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap justify-between gap-2">
            <Button variant="outline" disabled={cur === 0} onClick={() => setCur(cur - 1)} data-testid="cbt-prev-question-btn"><ChevronLeft className="h-4 w-4 mr-1" />Sebelumnya</Button>
            <Button variant="outline" onClick={() => setFlags({ ...flags, [q.id]: !flags[q.id] })} className={flags[q.id] ? "border-amber-500 text-amber-700" : ""} data-testid="cbt-flag-question-btn"><Flag className="h-4 w-4 mr-1" />Ragu-ragu</Button>
            {cur < qs.length - 1
              ? <Button className="bg-slate-900" onClick={() => setCur(cur + 1)} data-testid="cbt-next-question-btn">Selanjutnya<ChevronRight className="h-4 w-4 ml-1" /></Button>
              : <Button className="bg-amber-500 text-slate-900 hover:bg-amber-400" onClick={() => setConfirm(true)} data-testid="cbt-finish-button">Selesai</Button>}
          </div>
        </div>
        <aside className="lg:col-span-4 rounded-lg border border-slate-200 bg-white p-6 h-fit space-y-5">
          <p className="text-sm text-slate-600">Terjawab <span className="font-semibold text-slate-900" data-testid="cbt-answered-count">{answered}</span> dari {qs.length}</p>
          <QuestionGrid questions={qs} answers={answers} flags={flags} cur={cur} setCur={setCur} />
          <div className="flex gap-4 text-xs text-slate-500"><span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-slate-900" />Dijawab</span><span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-amber-400" />Ragu</span></div>
          <Button className="w-full bg-slate-900" onClick={() => setConfirm(true)} data-testid="cbt-submit-button">Kumpulkan Jawaban</Button>
        </aside>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="cbt-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kumpulkan jawaban?</AlertDialogTitle>
            <AlertDialogDescription>Anda menjawab {answered} dari {qs.length} soal{Object.values(flags).some(Boolean) ? " dan masih ada soal bertanda ragu-ragu" : ""}. Jawaban tidak dapat diubah setelah dikumpulkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cbt-confirm-cancel">Periksa lagi</AlertDialogCancel>
            <AlertDialogAction onClick={submit} className="bg-amber-500 text-slate-900 hover:bg-amber-400" data-testid="cbt-confirm-submit">Ya, kumpulkan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
